import { PANTHER_AI_NAME } from 'Source/constants';

/**
 * Data attribute used to mark elements that should be suppressed in print output.
 * Add data-print-suppress="true" to any component that shouldn't appear when printing.
 */
export const PRINT_SUPPRESS_ATTR = 'data-print-suppress';

/**
 * Data attribute used to mark Kendra entity link buttons.
 * These are converted to simple <code> elements when printing.
 */
export const KENDRA_ENTITY_LINK_ATTR = 'data-kendra-entity-link';

/**
 * Data attribute used to mark elements that should only appear in print output.
 * Add data-print-only="true" to any component that should be hidden on screen
 * but appear when printing.
 */
export const PRINT_ONLY_ATTR = 'data-print-only';

/**
 * Data attribute used to mark collapsible block type for conditional print filtering.
 * Value matches the blockType prop of CollapsibleBlock ('thinking' | 'analysis' | 'riskscore').
 */
export const PRINT_BLOCK_TYPE_ATTR = 'data-print-block-type';

/**
 * Data attribute used to mark message turns in conversation print output.
 * Add data-message-role="user" or "assistant" to message container elements.
 */
export const MESSAGE_ROLE_ATTR = 'data-message-role';
export const MESSAGE_ROLE = { USER: 'user', ASSISTANT: 'assistant' } as const;

/**
 * Collects computed styles from elements that need them preserved for printing.
 * Returns a map of element index to style object for elements that use CSS-in-JS.
 */
const collectComputedStyles = (content: HTMLElement) => {
  const styleMap = new Map<number, Record<string, string>>();

  // Collect styles from all elements with backgrounds (for gradient bars, etc.)
  const allElements = content.querySelectorAll('*');
  allElements.forEach((el, index) => {
    const computed = window.getComputedStyle(el);
    const styles: Record<string, string> = {};

    // Check for gradient backgrounds
    if (computed.backgroundImage && computed.backgroundImage !== 'none') {
      styles.backgroundImage = computed.backgroundImage;
      styles.background = computed.background;
    }

    // Check for theme colors that might not transfer
    if (computed.backgroundColor && computed.backgroundColor !== 'rgba(0, 0, 0, 0)') {
      styles.backgroundColor = computed.backgroundColor;
    }

    // Preserve padding-left for nested content indentation
    if (computed.paddingLeft && computed.paddingLeft !== '0px') {
      styles.paddingLeft = computed.paddingLeft;
    }

    if (Object.keys(styles).length > 0) {
      styleMap.set(index, styles);
    }
  });

  return styleMap;
};

/**
 * Applies collected styles to cloned elements
 */
const applyCollectedStyles = (
  clonedContent: HTMLElement,
  styleMap: Map<number, Record<string, string>>,
) => {
  const allElements = clonedContent.querySelectorAll('*');
  styleMap.forEach((styles, index) => {
    const el = allElements[index] as HTMLElement;
    if (el) {
      Object.entries(styles).forEach(([prop, value]) => {
        el.style.setProperty(prop.replace(/([A-Z])/g, '-$1').toLowerCase(), value);
      });
    }
  });
};

/**
 * Removes evidence (riskscore) collapsible blocks from cloned content.
 * Called when includeEvidence is false.
 */
const removeEvidenceElements = (clonedContent: HTMLElement) => {
  clonedContent
    .querySelectorAll(`[${PRINT_BLOCK_TYPE_ATTR}="riskscore"]`)
    .forEach(el => el.remove());
};

/**
 * Removes all elements marked with data-print-suppress attribute from cloned content.
 * This is the primary mechanism for suppressing interactive/injected elements in print.
 */
const removePrintSuppressedElements = (clonedContent: HTMLElement) => {
  clonedContent.querySelectorAll(`[${PRINT_SUPPRESS_ATTR}]`).forEach(el => el.remove());
};

/**
 * Makes print-only elements visible in print output.
 * These elements are hidden on screen but should appear when printing.
 */
const showPrintOnlyElements = (clonedContent: HTMLElement) => {
  clonedContent.querySelectorAll(`[${PRINT_ONLY_ATTR}]`).forEach(el => {
    const htmlEl = el as HTMLElement;
    htmlEl.style.display = 'block';
  });
};

/**
 * Converts Kendra entity link buttons to simple inline code elements for print.
 * Finds elements marked with data-kendra-entity-link and replaces them with <code> elements.
 */
const convertKendraEntityLinksForPrint = (clonedContent: HTMLElement) => {
  clonedContent.querySelectorAll(`[${KENDRA_ENTITY_LINK_ATTR}]`).forEach(el => {
    const codeEl = document.createElement('code');
    codeEl.textContent = el.textContent?.trim() || '';
    el.replaceWith(codeEl);
  });
};

/**
 * Collects indices of elements that need overflow fixes for collapsible blocks.
 * Must be called on the original content before cloning.
 * Skips elements that are inside a print-suppressed block (collapsed content).
 */
const collectCollapsibleBlockIndices = (content: HTMLElement): Set<number> => {
  const indicesToFix = new Set<number>();
  const allDivs = content.querySelectorAll('div');

  allDivs.forEach((div, index) => {
    // Skip elements that are inside a print-suppressed block (collapsed content)
    if (div.closest(`[${PRINT_SUPPRESS_ATTR}]`)) {
      return;
    }
    // Skip elements inside Mermaid diagram containers — their overflow:hidden is for
    // pan/zoom UX, not collapsible animation, and overriding it collapses the SVG viewport.
    if (div.closest('[data-testid="mermaid-diagram-card"]')) {
      return;
    }

    const computed = window.getComputedStyle(div);

    // If this div has overflow hidden, it might be a Collapse container
    if (
      computed.overflow === 'hidden' ||
      computed.visibility === 'hidden' ||
      computed.display === 'none'
    ) {
      indicesToFix.add(index);
    }
  });

  return indicesToFix;
};

/**
 * Fixes collapsible blocks (Analysis, Thinking, Evidence) for print.
 * The Collapse component uses CSS-in-JS that doesn't transfer to print windows.
 * This function forces content visible on elements identified by collectCollapsibleBlockIndices.
 * Skips elements inside print-suppressed blocks (collapsed content that shouldn't print).
 */
const fixCollapsibleBlocksForPrint = (clonedContent: HTMLElement, indicesToFix: Set<number>) => {
  const allDivs = clonedContent.querySelectorAll('div');

  allDivs.forEach((div, index) => {
    if (indicesToFix.has(index)) {
      const divEl = div as HTMLElement;
      // Force the content to be visible
      divEl.style.overflow = 'visible';
      divEl.style.height = 'auto';
      divEl.style.maxHeight = 'none';
      divEl.style.visibility = 'visible';
      divEl.style.display = 'block';
    }
  });

  // Additionally, fix elements with inline overflow:hidden style
  // This catches Collapse containers that may have been expanded but still have overflow constraints
  // Skip elements inside print-suppressed blocks
  clonedContent.querySelectorAll('*').forEach(el => {
    const htmlEl = el as HTMLElement;
    // Skip elements inside print-suppressed blocks
    if (htmlEl.closest(`[${PRINT_SUPPRESS_ATTR}]`)) {
      return;
    }
    // Skip elements inside Mermaid diagram containers
    if (htmlEl.closest('[data-testid="mermaid-diagram-card"]')) {
      return;
    }
    if (htmlEl.style) {
      if (htmlEl.style.overflow === 'hidden') {
        htmlEl.style.overflow = 'visible';
      }
      // Reset any height constraints that could cause clipping
      if (htmlEl.style.height && htmlEl.style.height !== 'auto') {
        htmlEl.style.height = 'auto';
      }
      if (htmlEl.style.maxHeight && htmlEl.style.maxHeight !== 'none') {
        htmlEl.style.maxHeight = 'none';
      }
    }
  });
};

/** Shared base styles used by both single-prompt and conversation print layouts */
const BASE_PRINT_STYLES = `
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    padding: 40px;
    max-width: 900px;
    margin: 0 auto;
    line-height: 1.6;
    color: #1a1a1a;
    font-size: 14px;
  }
  .header {
    border-bottom: 2px solid #e0e0e0;
    padding-bottom: 16px;
    margin-bottom: 24px;
  }
  .header h1 { margin: 0 0 8px 0; font-size: 24px; color: #1a1a1a; }
  .meta { color: #666; font-size: 12px; margin: 0; }
  .meta strong { color: #333; }
  .user-prompt {
    background: #f5f5f5;
    border-left: 4px solid #2196F3;
    padding: 12px 16px;
    margin: 16px 0 24px 0;
    font-style: italic;
    color: #333;
  }
  .content { color: #1a1a1a; }
  .content h1, .content h2, .content h3, .content h4 {
    margin-top: 24px; margin-bottom: 12px; color: #1a1a1a;
  }
  .content h1 { font-size: 20px; }
  .content h2 { font-size: 18px; }
  .content h3 { font-size: 16px; }
  .content p { margin: 12px 0; }
  .content ul, .content ol { margin: 12px 0; padding-left: 24px; }
  .content li { margin: 6px 0; }
  .content pre {
    background: #f8f8f8; border: 1px solid #e0e0e0; border-radius: 4px;
    padding: 16px; overflow-x: auto; font-size: 13px; line-height: 1.4;
  }
  .content code {
    background: #f0f0f0; padding: 2px 6px; border-radius: 3px;
    font-size: 13px; font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  }
  .content pre code { background: none; padding: 0; }
  .content table { border-collapse: collapse; width: 100%; margin: 16px 0; }
  .content th, .content td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; }
  .content th { background: #f5f5f5; font-weight: 600; }
  .content blockquote { border-left: 4px solid #ddd; margin: 16px 0; padding: 8px 16px; color: #666; }
  .content a { color: #2196F3; text-decoration: none; }
  .content img { max-width: 100%; height: auto; }
  .footer {
    margin-top: 32px; padding-top: 16px; border-top: 1px solid #e0e0e0;
    font-size: 11px; color: #999; text-align: center;
  }
  /* Mermaid diagrams: preserve the original dark-theme colors exactly as rendered.
     The card needs an explicit dark background so light-colored SVG elements
     (white/teal text, arrows) retain contrast against the dark field on paper. */
  [data-testid="mermaid-diagram-card"] {
    background-color: #1b2a3b !important;
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
    break-inside: avoid;
    page-break-inside: avoid;
    margin-top: 16px;
    margin-bottom: 16px;
  }
  [data-testid="mermaid-diagram-svg"] * {
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }
  @media print {
    body { padding: 20px; }
    .content pre { white-space: pre-wrap; word-wrap: break-word; }
    * { print-color-adjust: exact !important; -webkit-print-color-adjust: exact !important; }
  }
`;

/** Additional styles for conversation print layout */
const CONVERSATION_PRINT_STYLES = `
  .message-turn { margin-bottom: 4px; }
  .message-turn:last-child { margin-bottom: 0; }
  .message-turn + .message-turn { margin-top: 16px; }
  .turn-separator { border: none; border-top: 1px solid #e0e0e0; margin: 20px 0; }
  .role-label {
    font-size: 11px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.8px; margin-bottom: 6px;
  }
  .role-label.user {
    display: inline-block;
    background: #1565c0;
    color: #fff;
    padding: 3px 10px;
    border-radius: 12px;
    letter-spacing: 0.6px;
  }
  .role-label.assistant {
    display: inline-block;
    background: #5c6bc0;
    color: #fff;
    padding: 3px 10px;
    border-radius: 12px;
    letter-spacing: 0.6px;
  }
  .user-message {
    background: #e8f0fe; border-left: 4px solid #1565c0;
    padding: 10px 16px; border-radius: 0 6px 6px 0;
    white-space: pre-wrap; word-break: break-word; color: #1a1a1a;
    font-size: 14px; line-height: 1.5;
  }
  .assistant-message {
    background: #f3f4ff; border-left: 4px solid #5c6bc0;
    padding: 10px 16px; border-radius: 0 6px 6px 0;
    color: #1a1a1a; font-size: 14px; line-height: 1.5;
  }
  .assistant-message p:first-child { margin-top: 0; }
`;

/** Escapes HTML special characters to prevent XSS in print output */
const escapeHTML = (str: string): string =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Wraps body content in the shared print HTML document structure */
const wrapPrintHTML = (title: string, styles: string, bodyContent: string): string => `
  <!DOCTYPE html>
  <html>
    <head>
      <meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data: blob:;">
      <title>${escapeHTML(title)} - ${PANTHER_AI_NAME}</title>
      <style>${styles}</style>
    </head>
    <body>
      <div class="header">
        <h1>${escapeHTML(title)}</h1>
        <p class="meta">
          <strong>Generated:</strong> ${new Date().toLocaleString()}
        </p>
      </div>
      ${bodyContent}
      <div class="footer">
        Generated by ${PANTHER_AI_NAME} &bull; ${escapeHTML(window.location.origin)}
      </div>
    </body>
  </html>
`;

/**
 * Generates the print-friendly HTML content for single-prompt mode.
 */
const generatePrintHTML = (
  title: string,
  userPrompt: string | null | undefined,
  contentHTML: string,
): string =>
  wrapPrintHTML(
    title,
    BASE_PRINT_STYLES,
    `${
      userPrompt
        ? `<div class="user-prompt"><strong>Prompt:</strong> ${escapeHTML(userPrompt)}</div>`
        : ''
    }
        <div class="content">${contentHTML}</div>`,
  );

/**
 * Generates print-friendly HTML for conversation mode with multiple message turns.
 */
const generateConversationPrintHTML = (
  title: string,
  messageTurns: Array<{ userPrompt: string; responseHTML: string }>,
): string => {
  const turnsHTML = messageTurns
    .map(
      (turn, i) => `
      <div class="message-turn">
        <div class="role-label user">You</div>
        <div class="user-message">${escapeHTML(turn.userPrompt)}</div>
      </div>
      <div class="message-turn">
        <div class="role-label assistant">${PANTHER_AI_NAME}</div>
        <div class="assistant-message content">${turn.responseHTML}</div>
      </div>
      ${i < messageTurns.length - 1 ? '<hr class="turn-separator" />' : ''}`,
    )
    .join('');

  return wrapPrintHTML(title, `${BASE_PRINT_STYLES}${CONVERSATION_PRINT_STYLES}`, turnsHTML);
};

/**
 * Processes a cloned DOM element for print: fixes collapsible blocks, removes
 * suppressed elements, cleans up SVGs and buttons. Shared by both print paths.
 */
const processClonedContentForPrint = (
  original: HTMLElement,
  clone: HTMLElement,
  includeEvidence = false,
): void => {
  const styleMap = collectComputedStyles(original);
  const collapsibleIndices = collectCollapsibleBlockIndices(original);

  applyCollectedStyles(clone, styleMap);

  // Fix collapsible blocks BEFORE removing suppressed elements to keep indices aligned
  fixCollapsibleBlocksForPrint(clone, collapsibleIndices);
  removePrintSuppressedElements(clone);
  if (!includeEvidence) {
    removeEvidenceElements(clone);
  }
  showPrintOnlyElements(clone);
  convertKendraEntityLinksForPrint(clone);

  // Remove small SVG icons but preserve larger ones (mermaid diagrams)
  clone.querySelectorAll('svg').forEach(el => {
    const svg = el as SVGSVGElement;
    if (svg.closest('[data-testid="mermaid-diagram-svg"]')) {
      return;
    }
    const width = svg.getAttribute('width') ?? '0';
    const height = svg.getAttribute('height') ?? '0';
    if ((parseFloat(width) || 0) < 50 && (parseFloat(height) || 0) < 50) {
      el.remove();
    }
  });

  // Remove icon-only buttons (no text after SVG removal). Replace labeled toggle buttons
  // (Analysis, Thinking) with styled text headers in both single-prompt and conversation modes.
  clone.querySelectorAll('button').forEach(el => {
    const text = (el.textContent || '').trim();
    if (!text) {
      el.remove();
    } else {
      // Single-prompt mode: replace toggle button with a styled text header
      const header = document.createElement('div');
      header.style.cssText =
        'font-size:11px;font-weight:600;color:#5c6bc0;text-transform:uppercase;letter-spacing:0.7px;' +
        'border-left:3px solid #5c6bc0;padding:5px 10px;margin:16px 0 8px;background:#f3f4ff;border-radius:0 4px 4px 0;';
      header.textContent = text;
      el.replaceWith(header);
    }
  });
};

/**
 * Extracts conversation message turns from the DOM using data-message-role attributes.
 * Returns null if the content doesn't contain conversation markers.
 *
 * Walks elements in document order rather than pairing by index across two independent
 * NodeLists. This avoids mismatches when a turn is missing its assistant element (e.g.
 * a failed AI response mid-conversation).
 */
const extractConversationTurns = (
  content: HTMLElement,
  includeEvidence = false,
): Array<{ userPrompt: string; responseHTML: string }> | null => {
  const allRoleEls = content.querySelectorAll(`[${MESSAGE_ROLE_ATTR}]`);

  if (allRoleEls.length === 0) {
    return null;
  }

  const turns: Array<{ userPrompt: string; responseHTML: string }> = [];
  let i = 0;

  while (i < allRoleEls.length) {
    const el = allRoleEls[i];
    if (el.getAttribute(MESSAGE_ROLE_ATTR) === MESSAGE_ROLE.USER) {
      const userPrompt = (el.textContent || '').trim();
      let responseHTML = '';

      // Pair with the immediately following assistant element, if present
      const next = allRoleEls[i + 1];
      if (next?.getAttribute(MESSAGE_ROLE_ATTR) === MESSAGE_ROLE.ASSISTANT) {
        const assistantEl = next as HTMLElement;
        const clonedAssistant = assistantEl.cloneNode(true) as HTMLElement;
        processClonedContentForPrint(assistantEl, clonedAssistant, includeEvidence);
        // Safe to use innerHTML here: content originates from React's VDOM (no
        // dangerouslySetInnerHTML in the assistant subtree), so no raw HTML injection
        // is possible via server-supplied strings.
        responseHTML = clonedAssistant.innerHTML;
        i += 2;
      } else {
        i += 1;
      }

      if (userPrompt || responseHTML) {
        turns.push({ userPrompt: userPrompt || 'Triage Alert', responseHTML });
      }
    } else if (el.getAttribute(MESSAGE_ROLE_ATTR) === MESSAGE_ROLE.ASSISTANT) {
      // Orphan assistant element (no preceding user) — treat as auto-triggered triage
      const assistantEl = el as HTMLElement;
      const clonedAssistant = assistantEl.cloneNode(true) as HTMLElement;
      processClonedContentForPrint(assistantEl, clonedAssistant, includeEvidence);
      turns.push({ userPrompt: 'Triage Alert', responseHTML: clonedAssistant.innerHTML });
      i += 1;
    } else {
      i += 1;
    }
  }

  return turns.length > 0 ? turns : null;
};

export interface PrintAIResponseOptions {
  /** The title to display in the print header */
  title: string;
  /** The user's prompt that generated the response (optional, used in single-prompt mode) */
  userPrompt?: string | null;
  /** The ID of the element containing the AI response content (defaults to 'event-explanation') */
  contentElementId?: string;
  /** When true, Evidence (riskscore) collapsible blocks are included in print output. Defaults to false. */
  includeEvidence?: boolean;
}

/**
 * Opens a print-friendly window with the AI response content.
 *
 * Supports two modes:
 * - Single-prompt: Shows one user prompt + one AI response (legacy AI page)
 * - Conversation: Detects data-message-role attributes and renders each turn
 *   with clear visual separation between user prompts and AI responses
 */
export const printAIResponse = ({
  title,
  userPrompt,
  contentElementId = 'event-explanation',
  includeEvidence = false,
}: PrintAIResponseOptions): void => {
  // Try contentElementId first (respects explicit parameter), fall back to conversation-messages
  const content =
    document.getElementById(contentElementId) || document.getElementById('conversation-messages');
  if (!content) {
    return;
  }

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    return;
  }

  // Check for conversation mode: content has data-message-role markers
  const conversationTurns = extractConversationTurns(content, includeEvidence);

  let printHTML: string;
  if (conversationTurns) {
    // Conversation mode: structured turns with clear user/AI separation
    printHTML = generateConversationPrintHTML(title, conversationTurns);
  } else {
    // Single-prompt mode: original behavior
    const clonedContent = content.cloneNode(true) as HTMLElement;
    processClonedContentForPrint(content, clonedContent, includeEvidence);
    printHTML = generatePrintHTML(title, userPrompt, clonedContent.innerHTML);
  }

  printWindow.document.write(printHTML);
  printWindow.document.close();

  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
};
